<script>
  // @ is an alias to /src
import moment from 'moment'
import _ from 'lodash'
import { kitStatusLabels } from '../enums/kitItemStatus'
import { sampleStatusLabels } from '../enums/sampleStatus'
import kitService from '@/services/kitService'
import authService from '@/services/authService'

export default {
  name: 'Patients',
  props: {
    patientId: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      patientSearch: '',
      sortedBy: 'patientName',
      sortOrder: 'asc',
      kitsSelected: [],
      currentPatientIdentifier: '',
      recentlyOpened: false,
      checkboxes: {},
      expandAll: true,
      disableDateOfBirth: true
    }
  },
  computed: {
    hcp () {
      return this.$store.state.GET_HCP_RESPONSE
    },
    kits () {
      return this.$store.state.GET_KITS_RESPONSE
    },
    kitsByPatient () {
      // Remove kits with no coupled patient
      let filteredOnPatient = _.filter(this.kits, kit => kit.patientId)

      // Only use patients in the search
      if (this.patientSearch) {
        const searchFor = this.patientSearch.trim().toLowerCase()
        filteredOnPatient = _.filter(filteredOnPatient, kit => {
          return (
            (kit.patientName &&
              kit.patientName.toLowerCase().indexOf(searchFor) >= 0) ||
            (kit.kitName &&
              kit.kitName.toLowerCase().indexOf(searchFor) >= 0) ||
            kit.id.toString().indexOf(searchFor) >= 0
          )
        })
      }

      // Create an array of arrays sorted by patient name and date
      const sortedOnPatient = _.sortBy(filteredOnPatient, kit =>
        this.patientIdentifier(kit)
      )
      const kitsByPatient = []
      let kitsForThisPatient = []
      let currentPatient = '____random---patient+++string____'
      for (const kit of sortedOnPatient) {
        if (this.patientIdentifier(kit) !== currentPatient) {
          if (kitsForThisPatient.length > 0) {
            kitsByPatient.push(
              _.sortBy(
                kitsForThisPatient,
                kit => kit.reportAvailableDate
              ).reverse()
            )
          }
          currentPatient = this.patientIdentifier(kit)
          kitsForThisPatient = []
        }
        kitsForThisPatient.push(kit)
      }
      // Add the last one
      if (kitsForThisPatient.length > 0) {
        kitsByPatient.push(
          _.sortBy(
            kitsForThisPatient,
            kit => kit.reportAvailableDate
          ).reverse()
        )
      }

      // Sort the resultant array on either name or date of birth
      if (kitsByPatient.length <= 0) {
        return []
      }
      const kitsByPatientSortedAsc = _.sortBy(
        kitsByPatient,
        kitsForPatientArray => {
          return this.patientIdentifier(kitsForPatientArray[0], this.sortedBy)
        }
      )

      return this.sortOrder === 'asc'
        ? kitsByPatientSortedAsc
        : kitsByPatientSortedAsc.reverse()
    },
    enableCompare () {
      return !this.recentlyOpened && this.kitsSelected.length >= 2
    }
  },
  mounted () {
    this.$store.dispatch('getKits')

    // Keep track of recent accordion opening
    this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
      if (isJustShown) {
        this.recentlyOpened = true
      }
    })
  },
  methods: {
    sortBy (col) {
      if (this.sortedBy === col) {
        if (this.sortOrder === 'desc') {
          this.sortOrder = 'asc'
        } else {
          this.sortOrder = 'desc'
        }
      } else {
        this.sortedBy = col
        this.sortOrder = 'asc'
      }
    },
    formatDate (date) {
      const momentDate = date && moment(date)
      return moment.isMoment(momentDate) ? momentDate.format('D/M/YYYY') : ''
    },
    getStatus (kit) {
      if (kit.sampleStatus) {
        return sampleStatusLabels[kit.sampleStatus]
      }
      return kitStatusLabels[kit.kitStatus]
    },
    dateString (kit) {
      return kit.kitActivationDate
        ? moment(kit.kitActivationDate).format('DD MMMM YYYY')
        : 'Unredeemed'
    },
    kitSelection (selectedKitId) {
      if (!selectedKitId) {
        return
      }

      this.recentlyOpened = false

      // Is this another patient? If so, reset the selection
      const selectedKit = this.kits.find(kit => kit.id === selectedKitId)
      if (!selectedKit) {
        return
      }
      const selectedPatientIdentifier = this.patientIdentifier(selectedKit)
      if (this.currentPatientIdentifier !== selectedPatientIdentifier) {
        this.kitsSelected = []
        this.currentPatientIdentifier = selectedPatientIdentifier
      }

      // Was this a de-selection? If so, remove from the list
      if (this.kitsSelected.indexOf(selectedKitId) >= 0) {
        this.kitsSelected = this.kitsSelected.filter(
          kitId => kitId !== selectedKitId
        )
      } else {
        // Add kits to a max number, replacing the most recent one if necessary
        const index = this.kitsSelected.findIndex(id => id === selectedKitId)
        if (index < 0) {
          const maxAllowed = 2
          if (this.kitsSelected.length >= maxAllowed) {
            this.kitsSelected = this.kitsSelected.slice(0, maxAllowed - 1)
          }
          this.kitsSelected.push(selectedKitId)
        }
      }

      // Keep the checkboxes up to date with the new selection
      this.checkboxes = {}
      this.kitsSelected.forEach(kitId => {
        this.checkboxes[kitId] = kitId
      })
    },
    patientIdentifier (kit, primary = 'patientName') {
      if (primary === 'dateOfBirth') {
        return this.collapse(
          kit.dateOfBirth + kit.patientName + kit.patientId
        )
      }
      return this.collapse(kit.patientName + kit.dateOfBirth + kit.patientId)
    },
    openThisPatient (patientId) {
      return (
        this.expandAll ||
        (patientId &&
          this.patientId &&
          patientId.toString() === this.collapse(this.patientId))
      )
    },
    collapse (string) {
      return string && string.toLowerCase().replace(/ /g, '')
    },
    compareReports () {
      const sampleA = this.kits.find(kit => kit.id === this.kitsSelected[0])
      const sampleB = this.kits.find(kit => kit.id === this.kitsSelected[1])
      if (!sampleA || !sampleB) {
        console.error(
          'Error: one of the samples to compare was not found',
          sampleA,
          sampleB
        )
        return
      }

      this.$router.push({
        name: 'result',
        query: {
          samples: [sampleA?.sampleIdentifier, sampleB?.sampleIdentifier]
        }
      })
    },
    canViewReport (kit) {
      return kitService.canViewReport(kit)
    },
    betaTester (kitReportAvailableDate) {
      return kitService.betaTester(kitReportAvailableDate)
    },
    betaReportUrl (kit) {
      return `${localStorage.getItem('report_m')}${kit.sampleIdentifier}/${authService.getAccessToken()}`
    }
  }
}
</script>

<template>
  <div class="content-wrapper">
    <section class="content-customers pt-5 pb-5">
      <div class="container">
        <div class="row">
          <div class="col-9">
            <h2>Patients</h2>
          </div>

          <div class="col-3">
            <input
              v-model="patientSearch"
              class="float-right form-control form-control-sm"
              placeholder="Search Patients"
            >
          </div>
        </div>

        <div role="tablist">
          <template v-if="kitsByPatient && kitsByPatient.length > 0">
            <table class="table">
              <thead>
                <tr>
                  <th class="w-20">
                    <a
                      href="#"
                      @click.prevent="sortBy('patientName')"
                    >Name</a>
                  </th>
                  <th
                    v-if="!disableDateOfBirth"
                    class="w-10 text-right"
                  >
                    <a
                      href="#"
                      @click.prevent="sortBy('dateOfBirth')"
                    >Date of birth</a>
                  </th>
                  <th class="w-10">
                    Purchase Date
                  </th>
                  <th class="w-10">
                    Kit ID
                  </th>
                  <th class="">
                    Status
                  </th>
                  <th class="w-15">
                    Report Ready
                  </th>
                  <th class="w-15">
                    View & Compare
                  </th>
                </tr>
              </thead>
            </table>
            <div class="text-right mr-5">
              <span>
                <a
                  href="#"
                  class="toggle-report-title"
                  @click.prevent="expandAll = !expandAll"
                >{{ expandAll ? 'COLLAPSE ALL' : 'EXPAND ALL' }}
                  <i
                    :class="{
                      'fa fa-chevron-up': expandAll,
                      'fa fa-chevron-down': !expandAll
                    }"
                  />
                </a>
              </span>
            </div>
            <b-card
              v-for="(patientKits, patientKitsIndex) in kitsByPatient"
              :key="patientKits[0].id"
              no-body
              class="mb-0"
            >
              <b-card-header
                header-tag="header"
                class="p-0"
                role="tab"
              >
                <table class="table mb-0 pl-0 pt-0">
                  <tbody>
                    <tr>
                      <td class="w-20 font-weight-bold">
                        {{ patientKits[0].patientName }}
                      </td>
                      <td
                        v-if="!disableDateOfBirth"
                        class="w-10 text-right"
                      >
                        {{ formatDate(patientKits[0].dateOfBirth) }}
                      </td>
                      <td
                        v-b-toggle="`acc-${patientKitsIndex}`"
                        class="text-right"
                      >
                        <span
                          class="when-opened"
                        ><p class="toggle-report-title">HIDE REPORTS</p></span>
                        <span
                          class="when-closed"
                        ><p class="toggle-report-title">SHOW REPORTS</p></span>
                      </td>
                      <td
                        v-b-toggle="`acc-${patientKitsIndex}`"
                        class="w-5 text-right"
                      >
                        <span
                          class="when-opened toggle-report-title"
                        ><i class="fa fa-chevron-up" /></span>
                        <span
                          class="when-closed toggle-report-title"
                        ><i class="fa fa-chevron-down" /></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-card-header>
              <b-collapse
                :id="`acc-${patientKitsIndex}`"
                role="tabpanel"
                :visible="openThisPatient(patientKits[0].patientId)"
              >
                <b-card-body class="p-0">
                  <table class="table mb-0">
                    <tbody>
                      <!-- Rows per patient/kit -->
                      <tr
                        v-for="(kit, kitIndex) in patientKits"
                        :key="kit.id"
                        class="patient-report-row"
                      >
                        <td :class="!disableDateOfBirth ? 'w-30' : 'w-15'">
                          {{ kit.kitName || dateString(kit) }}
                        </td>
                        <td class="w-10">
                          {{ formatDate(kit.purchaseDate) }}
                        </td>
                        <td class="w-10">
                          {{ kit.id }}
                        </td>
                        <td class="w-20">
                          <b>{{ getStatus(kit) }}</b>
                        </td>
                        <td class="w-10">
                          <b>{{ formatDate(kit.reportAvailableDate) }}</b>
                        </td>
                        <template v-if="canViewReport(kit)">
                          <td class="w-15 text-right">
                            <router-link
                              :to="{
                                name: 'result',
                                query: {samples: [kit.sampleIdentifier]}
                              }"
                            >
                              <b>VIEW REPORT</b>
                            </router-link>
                            <br>
                            <a
                              v-if="betaTester(formatDate(kit.reportAvailableDate))"
                              target="_blank"
                              :href="betaReportUrl(kit)"
                            >
                              <b><c>VIEW BETA REPORT <i class="fas fa-external-link-alt" /></c></b>
                            </a>
                          </td>
                          <td
                            :id="`td-cb-${patientKitsIndex}-${kitIndex}`"
                            class="w-5"
                          >
                            <b-form-checkbox
                              v-model="checkboxes[kit.id]"
                              :value="kit.id"
                              class="kit-checkbox"
                              @change="kitSelection(kit.id)"
                            />
                            <b-tooltip
                              :target="`td-cb-${patientKitsIndex}-${kitIndex}`"
                              triggers="hover"
                              delay="1000"
                            >
                              Select two patient reports to compare
                            </b-tooltip>
                          </td>
                        </template>
                        <template v-else>
                          <td class="w-15 text-right" />
                          <td class="w-5" />
                        </template>
                      </tr>

                      <!-- The Compare button -->
                      <tr>
                        <td
                          colspan="7"
                          class="text-right"
                        >
                          <b-button
                            :disabled="!enableCompare"
                            class="purple"
                            @click="compareReports"
                          >
                            Compare Reports
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </b-card-body>
              </b-collapse>
            </b-card>
          </template>
          <template v-else>
            <p><b>You don't currently have any patients with a kit</b></p>
            <br>
          </template>
        </div>
      </div>
      <!-- /CONTAINER -->
    </section>
  </div>
</template>

<style>
  tr a {
    color: #5d4164;
  }

  tr c {
    color: #c203f1;
  }

  th a {
    color: white;
    text-decoration: underline;
  }

  th,
  td {
    vertical-align: middle;
  }

  table thead tr {
    background-color: #00193d;
    border: 0;
    color: white;
  }

  .collapsed > .when-opened,
  :not(.collapsed) > .when-closed {
    display: none;
  }

  .toggle-report-title {
    font-weight: 600;
    font-size: 14px;
    color: #ff8708;
    margin-bottom: 0;
  }

  .patient-report-row {
    background-color: #eee;
  }

  .purple {
    background-color: #5d4164;
  }

  .purple.disabled {
    background-color: #645c62;
  }

  .custom-control-input ~ .custom-control-label::before {
    border-radius: 10px;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #5cd69f;
    border-color: #5cd69f;
    border-radius: 10px;
  }

  .table td,
  .table th {
    outline: none;
  }
</style>

<style scoped>
  .disabled-report {
    text-decoration: line-through;
    cursor: default;
    font-weight: bold;
  }
</style>
